import React, { useContext, useMemo, useState } from "react";
import { ActionMenu, ConfirmNotification, TableView1 } from "@/components";
import { bool, string } from "prop-types";
import { Button, Stack } from "@mui/material";
import { useTextDomainContext } from "@/app/providers";
import { AddDataLimit } from "../";
import { useDataLimitList, useDeleteDataLimit } from "@/hooks";
import { dataLimitFormat } from "@/utils";

export interface DataLimitsProps {
  showAdd?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  parentPath?: string;
}

export const DataLimits = (props: DataLimitsProps) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const { dataLimitList, loading } = useDataLimitList();
  const { deleteDataLimit } = useDeleteDataLimit();

  // const rows = [];
  // const loading = false;
  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
        name: true,
        consumption: true,
        climate: true,
        Procurement: true,
        statement: true,
      },
    },
  };

  //#region useMemo

  const showAdd = useMemo(() => {
    return props.showAdd || false;
  }, [props.showAdd]);

  // const showEdit = useMemo(() => {
  //   return props.showEdit || false;
  // }, [props.showEdit]);

  // const showDelete = useMemo(() => {
  //   return props.showDelete || false;
  // }, [props.showDelete]);

  //#endregion useMemo

  //#region React Hook (useState)
  interface DataLimitsRow {
    Id?: string | number;
    id?: string | number;
    name?: string;
    [key: string]: any;
  }

  const [addFormOpen, setAddFormOpen] = useState<boolean>(false);
  const [editRow, setEditRow] = useState<DataLimitsRow | {}>({});
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [dialogParameters, setDialogParameters] = useState<any>({});
  const [confirmMessage, setConfirmMessage] = useState<string>("");
  //#endregion React Hook (useState)

  const rows = dataLimitList || [];

  //#region useEffect
  // useEffect(() => {
  //   if (!isEqual(rows, dataLimitList)) {
  //     setRows(dataLimitList);
  //   }
  // }, [dataLimitList, rows]);

  //#endregion useEffect

  //#region Handlers
  const postToolbar = () => {
    return (
      <>
        <Stack direction="row" spacing={1} sx={{ pl: 1, alignItems: "center" }}>
          {showAdd && (
            <Button
              variant="contained"
              onClick={() => {
                setAddFormOpen(true);
              }}
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {gettext("Add Historical Data Limit")}
            </Button>
          )}
        </Stack>
      </>
    );
  };

  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
      checkboxSelection: true,
    },
    {
      field: "name",
      headerName: gettext("Name"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "consumption",
      headerName: gettext("Consumption"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
      valueGetter: (obj: any) => dataLimitFormat(obj, gettext),
    },
    {
      field: "climate",
      headerName: gettext("Climate"),
      flex: 1,
      // minWidth: 150,
      headerAlign: "left",
      type: "string",
      valueGetter: (obj: any) => dataLimitFormat(obj, gettext),
    },
    {
      field: "procurement",
      headerName: gettext("Cost"),
      flex: 1,
      headerAlign: "left",
      type: "string",
      valueGetter: (obj: any) => dataLimitFormat(obj, gettext),
    },
    {
      field: "statement",
      headerName: gettext("Excel Reports"),
      flex: 1,
      headerAlign: "left",
      type: "string",
      valueGetter: (obj: any) => dataLimitFormat(obj, gettext),
    },
    {
      field: "actions",
      headerName: gettext("Actions"),
      hideable: false,
      minWidth: 120,
      maxWidth: 75,
      align: "right",
      headerAlign: "right",
      type: "actions",
      // renderCell: (params) => columnFormatter(params),
      renderCell: (params: any) => (
        <ActionMenu
          menuFor="data-limit"
          row={params.row}
          menuClick={menuClick}
        />
      ),
    },
  ];

  // const columnFormatter = (params) => {
  //   return (
  //     <div className="w-100 d-flex justify-content-center">
  //       {showEdit && (
  //         <Tooltip title={gettext("Edit")} arrow>
  //           <IconButton onClick={() => performAction(params.row, "edit")}>
  //             <FaUserEdit size={16} />
  //           </IconButton>
  //         </Tooltip>
  //       )}
  //
  //       {showDelete && (
  //         <Tooltip title={gettext("Delete")} arrow>
  //           <IconButton onClick={() => performAction(params.row, "delete")}>
  //             <MdDelete size={16} />
  //           </IconButton>
  //         </Tooltip>
  //       )}
  //     </div>
  //   );
  // };

  const menuClick = (action: string, row: any) => {
    switch (action) {
      case "data-limit-delete":
        handleOnClickDeleteSingle(row);
        // handleOnClickRemoveLink(action, row?.id);
        break;
      case "data-limit-edit":
        setEditRow(row);
        setAddFormOpen(true);
        break;
      default:
        break;
    }
  };

  // const performAction = (row, action) => {
  //   switch (action) {
  //     case "edit":
  //       handleEdit(row);
  //       break;
  //     case "delete":
  //       handleOnClickDeleteSingle(row);
  //       break;
  //     default:
  //       break;
  //   }
  // };

  // const handleEdit = (row) => {
  //   setEditRow(row);
  //   setAddFormOpen(true);
  // };

  const handleOnClickDeleteSingle = (row: any) => {
    setConfirmMessage(
      gettext("Do you want to delete the historical data limit?"),
    );
    setDialogParameters({
      action: "delete-single",
      id: row.Id,
    });
    setShowConfirm(true);
  };

  const handleGetResult = (obj: any) => {
    if (obj.result === "yes") {
      deleteDataLimit({
        dataLimitId: obj?.parameters?.id,
      });
    }
    setShowConfirm(false);
  };

  const handleCloseAddForm = (event: boolean) => {
    setAddFormOpen(event);
    setEditRow({});
  };
  //#endregion Handlers

  return (
    <>
      <TableView1
        title={gettext("Historical Data Limits")}
        rows={rows}
        columns={columns}
        loading={loading}
        initialState={initialState}
        postToolbar={postToolbar()}
        checkboxSelection={false}
        showGridToolbar={true}
        showGridToolbarDeleteButton={false}
        showGridToolbarDensitySelector={false}
        showGridToolbarColumnsButton={false}
        showGridToolbarQuickFilter
        showGridToolbarFilterButton={false}
        showGridToolbarExport={false}
      />

      <AddDataLimit
        open={addFormOpen}
        handleClose={handleCloseAddForm}
        row={editRow}
      />

      <ConfirmNotification
        isOpen={showConfirm}
        parameters={dialogParameters}
        getResult={handleGetResult}
        message={confirmMessage}
      />

      {/*{deleting ? (*/}
      {/*  <div className="position-absolute bg-dark opacity-50 w-100 h-100 d-flex justify-content-center align-items-center">*/}
      {/*    /!*<Spinner animation="border" variant="light" />*!/*/}
      {/*  </div>*/}
      {/*) : null}*/}
    </>
  );
};

DataLimits.propTypes = {
  showAdd: bool,
  showEdit: bool,
  showDelete: bool,
  parentPath: string,
};
